import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  cancelEvalExperimentMutation,
  evaluateEvalExperimentMutation,
  getEvalExperimentOptions,
  listEvalFixturesOptions,
  setEvalBaselineMutation,
} from '@/api/generated/@tanstack/react-query.gen';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EvalShell, Panel, useEvaluatorName } from '@/components/Evals/EvalShell';
import { Delta, pct, scoreTone, StatusPill } from '@/components/Evals/format';
import { RunsTable } from '@/components/Evals/RunsTable';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/evals/experiments/$experimentId')({
  component: ExperimentPage,
});

function ExperimentPage() {
  const { experimentId } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    ...getEvalExperimentOptions({ path: { experiment_id: experimentId } }),
    refetchInterval: (q) =>
      q.state.data?.runs.some((r) => ['queued', 'running', 'evaluating'].includes(r.status)) ? 5000 : false,
  });
  const fixtures = useQuery(listEvalFixturesOptions());
  const refresh = () => queryClient.invalidateQueries();
  const baseline = useMutation({ ...setEvalBaselineMutation(), onSuccess: () => (toast.success('Now the baseline'), refresh()) });
  const evaluate = useMutation({
    ...evaluateEvalExperimentMutation(),
    onSuccess: (r) => (toast.success(`${r.updated} runs queued for scoring`), refresh()),
  });
  const cancel = useMutation({ ...cancelEvalExperimentMutation(), onSuccess: () => (toast.success('Cancelled'), refresh()) });
  const path = { path: { experiment_id: experimentId } };
  const name = useEvaluatorName();

  const data = query.data;
  const evaluatorKeys = useMemo(
    () => [...new Set((data?.evaluator_means ?? []).map((m) => m.evaluator_key))].sort(),
    [data?.evaluator_means],
  );
  const fixtureIds = data?.experiment.fixture_ids ?? [];
  const fixtureName = (id: string) => fixtures.data?.find((f) => f.id === id)?.name ?? id;
  const isActive = data?.runs.some((r) => ['queued', 'running'].includes(r.status));

  return (
    <EvalShell
      title={data?.experiment.name ?? 'Experiment'}
      crumbs={[{ label: 'Experiments', to: '/evals/experiments' }]}
      subtitle={data?.experiment.notes || undefined}
      actions={
        data && (
          <>
            {isActive && (
              <Button variant="outline" className="h-9 rounded-full" onClick={() => cancel.mutate(path)}>
                Cancel runs
              </Button>
            )}
            <Button variant="outline" className="h-9 rounded-full" onClick={() => evaluate.mutate(path)}>
              Re-score
            </Button>
            <Button
              className="btn-3d h-9 rounded-full px-5"
              disabled={(data.experiment.baseline_for ?? []).length === fixtureIds.length}
              onClick={() => baseline.mutate(path)}
            >
              {(data.experiment.baseline_for ?? []).length === fixtureIds.length ? 'Baseline' : 'Set as baseline'}
            </Button>
          </>
        )
      }
    >
      {!data ? (
        <Skeleton className="h-96 rounded-3xl" />
      ) : (
        <>
          <div className="flex flex-wrap gap-2 text-[12px] text-neutral-500">
            {Object.entries(data.experiment.run_counts ?? {}).map(([status, n]) => (
              <span key={status} className="flex items-center gap-1">
                <StatusPill status={status} /> {n}
              </span>
            ))}
            <span>· learners {data.experiment.personas.join(', ')}</span>
            <span>· voices {data.experiment.learner_voices.join(', ')}</span>
            <span>· {data.experiment.turns} turns</span>
            {data.experiment.git_sha && <span className="font-mono">· {data.experiment.git_sha}</span>}
            {data.experiment.config?.llm_model && (
              <span className="rounded-full bg-violet-50 px-2 text-violet-700">model {data.experiment.config.llm_model}</span>
            )}
            {Object.entries(data.experiment.config?.prompt_overrides ?? {})
              .filter(([, v]) => v)
              .map(([k]) => (
                <span key={k} className="rounded-full bg-violet-50 px-2 text-violet-700">
                  {k.replace(/_/g, ' ')} prompt edited
                </span>
              ))}
          </div>

          <Panel title="Scores vs baseline">
            {!evaluatorKeys.length ? (
              <p className="text-sm text-neutral-400">No scored runs yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[13px]">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-neutral-400">
                      <th className="py-1.5 pr-3 font-medium">Evaluator</th>
                      {fixtureIds.map((id) => (
                        <th key={id} className="py-1.5 pr-3 font-medium">
                          {fixtureName(id)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {evaluatorKeys.map((key) => (
                      <tr key={key} className="border-t border-neutral-100">
                        <td className="py-1.5 pr-3 text-neutral-700">{name(key)}</td>
                        {fixtureIds.map((id) => {
                          const m = (data.evaluator_means ?? []).find((x) => x.fixture_id === id && x.evaluator_key === key);
                          return (
                            <td key={id} className="py-1.5 pr-3">
                              <span className={cn('rounded-full px-2 py-0.5 text-[12px] font-medium', scoreTone(m?.mean))}>
                                {pct(m?.mean)}
                              </span>{' '}
                              <Delta value={m?.mean} baseline={m?.baseline_mean} />
                              {m?.baseline_mean != null && (
                                <span className="ml-1 text-[11px] text-neutral-400">base {pct(m.baseline_mean)}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title={`Runs · ${data.runs.length}`}>
            <RunsTable runs={data.runs} showLastLine={isActive} />
          </Panel>
        </>
      )}
    </EvalShell>
  );
}
